import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileCode, AlertCircle, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useChains } from '@/hooks/useChains'
import { verifyContract } from '@/services/api'
import { processUploadedFiles, createMetadataFile } from '@/utils/metadata'
import type { VerificationResult } from '@/types'

export function VerificationForm() {
  const { chains, loading: chainsLoading } = useChains()
  const [address, setAddress] = useState('')
  const [selectedChain, setSelectedChain] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [verifying, setVerifying] = useState(false)
  const [result, setResult] = useState<VerificationResult | null>(null)
  const [fileStatus, setFileStatus] = useState<{
    hasMetadata: boolean;
    hardhatDetected: boolean;
    sourceCount: number;
  } | null>(null)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/plain': ['.sol', '.json'],
    },
    onDrop: async (acceptedFiles) => {
      setFiles(prev => [...prev, ...acceptedFiles])

      // Analyze the uploaded files
      const allFiles = [...files, ...acceptedFiles]
      const { metadata, sources, hardhatArtifactDetected } = await processUploadedFiles(allFiles)

      setFileStatus({
        hasMetadata: metadata !== null,
        hardhatDetected: hardhatArtifactDetected,
        sourceCount: sources.length,
      })
    },
  })

  const handleVerify = async () => {
    if (!address || !selectedChain || files.length === 0) {
      return
    }

    setVerifying(true)
    setResult(null)

    try {
      // Process files to extract metadata from Hardhat artifacts if needed
      const { metadata, sources, hardhatArtifactDetected } = await processUploadedFiles(files)

      if (!metadata) {
        setResult({
          status: 'error',
          message: 'No metadata found. Please upload metadata.json or a Hardhat artifact file.',
        })
        setVerifying(false)
        return
      }

      // Create the files array to send to the API
      const filesToSend: File[] = [...sources]

      // If we extracted metadata from Hardhat artifact, create a proper metadata.json file
      if (hardhatArtifactDetected) {
        filesToSend.push(createMetadataFile(metadata))
      } else {
        // Include the original metadata file
        const metadataFile = files.find(f => f.name.endsWith('.json'))
        if (metadataFile) {
          filesToSend.push(metadataFile)
        }
      }

      const response = await verifyContract({
        address,
        chain: selectedChain,
        files: filesToSend,
      })
      setResult(response)
    } catch (error) {
      setResult({
        status: 'error',
        message: error instanceof Error ? error.message : 'Verification failed',
      })
    } finally {
      setVerifying(false)
    }
  }

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Verify Smart Contract</CardTitle>
        <CardDescription>
          Upload your contract source files and metadata.json to verify against deployed bytecode
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="chain">Blockchain Network</Label>
          <select
            id="chain"
            value={selectedChain}
            onChange={(e) => setSelectedChain(e.target.value)}
            disabled={chainsLoading}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Select a network...</option>
            {chains.map((chain) => (
              <option key={chain.chainId} value={chain.chainId}>
                {chain.name} ({chain.shortName})
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Contract Address</Label>
          <Input
            id="address"
            placeholder="0x..."
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>Source Files</Label>
          <div className="mb-2 p-3 bg-muted rounded-md">
            <p className="text-xs text-muted-foreground">
              <strong>Required:</strong> Upload metadata.json (from compiler output) and all .sol source files
            </p>
          </div>
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            {isDragActive ? (
              <p className="text-sm text-muted-foreground">Drop files here...</p>
            ) : (
              <div>
                <p className="text-sm font-medium">Drag & drop files here</p>
                <p className="text-xs text-muted-foreground mt-1">
                  or click to browse (.sol, .json files)
                </p>
              </div>
            )}
          </div>

          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {fileStatus && (
                <div className={`p-3 rounded-md ${fileStatus.hasMetadata ? 'bg-green-50 dark:bg-green-950' : 'bg-yellow-50 dark:bg-yellow-950'}`}>
                  <div className="flex items-center gap-2">
                    {fileStatus.hasMetadata ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-yellow-600" />
                    )}
                    <span className="text-xs font-medium">
                      {fileStatus.hasMetadata
                        ? `Ready: ${fileStatus.hardhatDetected ? 'Hardhat artifact detected, ' : ''}${fileStatus.sourceCount} source file(s)`
                        : 'Missing metadata.json or Hardhat artifact'}
                    </span>
                  </div>
                </div>
              )}
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded border bg-muted/50"
                >
                  <div className="flex items-center gap-2">
                    <FileCode className="h-4 w-4" />
                    <span className="text-sm">{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          onClick={handleVerify}
          disabled={!address || !selectedChain || files.length === 0 || verifying}
          className="w-full"
        >
          {verifying ? 'Verifying...' : 'Verify Contract'}
        </Button>

        {result && (
          <div
            className={`p-4 rounded-lg border ${
              result.status === 'success'
                ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                : 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'
            }`}
          >
            <p className="text-sm font-medium">
              {result.status === 'success' ? '✓ Verification Successful' : '✗ Verification Failed'}
            </p>
            {result.message && (
              <p className="text-sm text-muted-foreground mt-1">{result.message}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
